import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Home, 
  RefreshCw, 
  TrendingUp, 
  Users, 
  Building2, 
  ArrowRight,
  CheckCircle,
  Calculator,
  FileText,
  DollarSign
} from 'lucide-react';

const services = [
  {
    id: 'home-loans',
    icon: Home,
    title: 'Home Loans',
    subtitle: 'Find your dream home',
    description: 'Whether you\'re buying your first home or upgrading to something bigger, we\'ll help you find a competitive home loan that fits your budget and lifestyle.',
    features: [
      'Variable rate loans with flexible repayments',
      'Fixed rate loans for repayment certainty',
      'Split loans combining variable and fixed',
      'Low deposit options (as little as 5%)',
      'Offset accounts to reduce interest',
      'Redraw facilities for extra payments'
    ],
    benefits: [
      'Compare rates from 40+ lenders',
      'No broker fees to pay',
      'Expert guidance throughout the process'
    ],
    image: 'https://images.unsplash.com/photo-1737622088520-895463a1a06f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhdXN0cmFsaWFuJTIwaG91c2UlMjBleHRlcmlvciUyMGFyY2hpdGVjdHVyZSUyMHN1bm55fGVufDB8fHx8MTc3MDAwMjc1OXww&ixlib=rb-4.1.0&q=85'
  },
  {
    id: 'refinancing',
    icon: RefreshCw,
    title: 'Refinancing',
    subtitle: 'Could you be paying less?',
    description: 'Interest rates change, and so do your circumstances. Refinancing could help you secure a better rate, access equity, or restructure your loan.',
    features: [
      'Switch to a lower interest rate',
      'Consolidate multiple debts',
      'Access home equity for renovations',
      'Change loan features (offset, redraw)',
      'Switch between fixed and variable',
      'Reduce your loan term'
    ],
    benefits: [
      'Free home loan health check',
      'Calculate your potential savings',
      'Hassle-free switching process'
    ],
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop&q=80'
  },
  {
    id: 'investment-loans',
    icon: TrendingUp,
    title: 'Investment Property Loans',
    subtitle: 'Build your property portfolio',
    description: 'Property investment is one of the most popular wealth-building strategies in Australia. We\'ll help you structure your investment loans for maximum benefit.',
    features: [
      'Interest-only loan options',
      'Principal and interest loans',
      'Line of credit facilities',
      'Cross-collateralization strategies',
      'Equity release for deposits',
      'Tax-effective loan structuring'
    ],
    benefits: [
      'Investment property expertise',
      'Tax implications explained',
      'Portfolio growth strategies'
    ],
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&auto=format&fit=crop&q=80'
  },
  {
    id: 'first-home-buyer',
    icon: Users,
    title: 'First Home Buyer',
    subtitle: 'Your first step on the property ladder',
    description: 'Buying your first home is exciting but can be overwhelming. We specialise in guiding first home buyers through every step, from grants to settlement.',
    features: [
      'First Home Owner Grant (FHOG) assistance',
      'First Home Guarantee scheme access',
      'Stamp duty concessions guidance',
      'Low deposit loan options (5-10%)',
      'Lenders Mortgage Insurance explained',
      'Guarantor loan arrangements'
    ],
    benefits: [
      'Step-by-step guidance',
      'Government grant maximisation',
      'First home buyer specialists'
    ],
    image: 'https://images.unsplash.com/photo-1758523671087-b256bbbca475?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGZhbWlseSUyMG1vdmluZyUyMGludG8lMjBuZXclMjBob21lJTIwYm94ZXN8ZW58MHx8fHwxNzcwMDAyNzg1fDA&ixlib=rb-4.1.0&q=85'
  },
  {
    id: 'commercial-loans',
    icon: Building2,
    title: 'Commercial Loans',
    subtitle: 'Finance for business and property',
    description: 'Whether you\'re buying business premises, investing in commercial property, or need development finance, we have the expertise to structure the right solution.',
    features: [
      'Owner-occupied business premises',
      'Commercial investment property',
      'Development and construction finance',
      'SMSF property purchases',
      'Business asset finance',
      'Mixed-use property loans'
    ],
    benefits: [
      'Commercial lending specialists',
      'Access to specialist lenders',
      'Tailored business solutions'
    ],
    image: 'https://images.unsplash.com/photo-1681505531034-8d67054e07f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBmaW5hbmNpYWwlMjBtZWV0aW5nJTIwaGFuZHNoYWtlfGVufDB8fHx8MTc3MDAwMjc2N3ww&ixlib=rb-4.1.0&q=85'
  }
];

const processSteps = [
  {
    icon: Calculator,
    title: 'Calculate',
    description: 'We assess your borrowing capacity and compare options from 40+ lenders.'
  },
  {
    icon: FileText,
    title: 'Apply',
    description: 'We handle the paperwork and submit your application to the right lender.'
  },
  {
    icon: CheckCircle,
    title: 'Approve',
    description: 'We follow up to ensure a smooth approval process.'
  },
  {
    icon: DollarSign,
    title: 'Settle',
    description: 'We coordinate with all parties for a hassle-free settlement.'
  }
];

export default function ServicesPage() {
  return (
    <div className="pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 gradient-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-[#374151]/10 text-[#374151] rounded-full text-sm font-medium mb-6">
              Our Services
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-[#374151] leading-tight mb-6">
              Comprehensive Lending Solutions
            </h1>
            <p className="text-lg md:text-xl text-gray-500 leading-relaxed">
              From first homes to investment portfolios, we provide expert mortgage broking services 
              tailored to your unique financial goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-semibold text-[#374151]">
              Our Simple 4-Step Process
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center relative"
              >
                <div className="w-16 h-16 bg-[#F3F4F6] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-8 h-8 text-[#374151]" />
                </div>
                <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-8 h-8 bg-[#6B7280] text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {index + 1}
                </span>
                <h3 className="font-heading font-semibold text-lg text-[#374151] mb-2">{step.title}</h3>
                <p className="text-sm text-gray-500">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Detail Sections */}
      {services.map((service, index) => (
        <section 
          key={service.id}
          id={service.id}
          className={`py-20 md:py-32 ${index % 2 === 0 ? 'bg-[#F9FAFB]' : 'bg-white'}`}
          data-testid={`service-section-${service.id}`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
              index % 2 === 0 ? '' : 'lg:flex-row-reverse'
            }`}>
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={index % 2 === 0 ? '' : 'lg:order-2'}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#F3F4F6] rounded-xl flex items-center justify-center">
                    <service.icon className="w-6 h-6 text-[#374151]" />
                  </div>
                  <span className="text-[#6B7280] font-medium text-sm uppercase tracking-wide">
                    {service.subtitle}
                  </span>
                </div>
                
                <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#374151] mb-6">
                  {service.title}
                </h2>
                
                <p className="text-lg text-gray-500 leading-relaxed mb-8">
                  {service.description}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {service.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-[#6B7280] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-500 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-[#F3F4F6]/50 rounded-xl p-6 mb-8">
                  <h4 className="font-heading font-semibold text-[#374151] mb-3">Why Choose Us?</h4>
                  <ul className="space-y-2">
                    {service.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-[#6B7280] rounded-full"></span>
                        <span className="text-gray-500 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link to="/contact">
                  <Button 
                    className="bg-[#6B7280] hover:bg-[#4B5563] text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                    data-testid={`cta-${service.id}`}
                  >
                    Enquire Now
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </motion.div>

              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={index % 2 === 0 ? '' : 'lg:order-1'}
              >
                <div className="rounded-2xl overflow-hidden shadow-hover">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-[400px] object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#374151]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
              Not Sure Which Service You Need?
            </h2>
            <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
              Our mortgage experts are here to help. Book a free consultation and we'll assess your situation 
              to recommend the best solution for your needs.
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-[#6B7280] hover:bg-[#4B5563] text-white rounded-full px-10 py-7 text-lg font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                data-testid="services-cta"
              >
                Book Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
